import React, { useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { gsap } from "gsap";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Import custom styles
import "./HeroSlider.css"; // Ensure this path is correct

const HeroSlider = () => {
  const slideRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Get base URL dynamically
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const slideData = useMemo(
    () => [
      {
        title: "Krafty Chenille Baby",
        img: "./chenille.jpg",
        text: "Luxuriously soft, perfect for delicate and cuddly creations.",
        productSlug: "krafty-baby-chenille-fine",
      },
      {
        title: "Krafty baby milk cotton",
        img: "./krafty-milk-cotton.jpg",
        text: "designed for the softest, snuggliest creations.",
        productSlug: "krafty-baby-milk-cotton",
      },
      {
        title: "T Shirt Yarn",
        img: "./tshirt-yarn.webp",
        text: "Soft and stretchy, ideal for crafting bags, rugs, and home decor.",
        productSlug: "t-shirt-yarn",
      },
      {
        title: "Softly Baby",
        img: "./softly-baby.webp",
        text: "Light and airy, designed for crafting tender, cuddly projects.",
        productSlug: "softly-baby",
      },
      {
        title: "Stylecraft Special DK",
        img: "./stylecraft-dk.jpg",
        text: "rich colours and superior softness for all your crafting needs.",
        productSlug: "stylecraft-special-dk",
      },
      {
        title: "Papatya Cake Silver",
        img: "./papatya-cake.jpg",
        text: "Perfect for making all kinds of projects, from cardigans and blankets to scarves, sweaters and more!",
        productSlug: "papatya-cake-silver",
      },
      {
        title: "Cicibebe Batik",
        img: "./cicibebe-batik.jpg",
        text: "Richly coloured batik yarn, soft and perfect for unique craftwork.",
        productSlug: "cicibebe-batik",
      },
    ],
    []
  );

  // Generate dynamic links
  const generateProductLink = (productSlug) => {
    return `${baseUrl}/products/${productSlug}`;
  };

  useEffect(() => {
    // Check if the target element exists before animating
    if (slideRefs.current[activeIndex]) {
      // Animate the active slide's content
      gsap.fromTo(
        slideRefs.current[activeIndex],
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
        }
      );
    }

    // Reset animation for previous slides
    slideData.forEach((_, index) => {
      if (index !== activeIndex && slideRefs.current[index]) {
        gsap.to(slideRefs.current[index], {
          opacity: 0,
          y: -50,
          duration: 0.5,
          ease: "power3.in",
        });
      }
    });
  }, [activeIndex, slideData]);

  return (
    <div className="hero-slider-container w-full">
      <Swiper
        modules={[Autoplay, Pagination]}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        loop={true}
        pagination={{ clickable: true }}
        className="hero-swiper"
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
      >
        {slideData.map((slide, index) => (
          <SwiperSlide key={index}>
            <div
              className="slide relative w-full h-[60vh] md:h-[90vh] bg-cover bg-center flex items-center justify-center"
              style={{
                backgroundImage: `url(${slide.img})`,
              }}
            >
              {/* Overlay */}
              <div className="overlay absolute inset-0 bg-black opacity-50"></div>

              {/* Slide Content */}
              <div
                ref={(el) => (slideRefs.current[index] = el)}
                className="slide-content z-10 text-center px-4 opacity-0 transform -translate-y-10"
              >
                <h1 className="slide-title font-semibold font-noto text-white text-2xl md:text-5xl mb-2">
                  {slide.title}
                </h1>
                <p className="slide-text text-white text-md md:text-lg mb-16">
                  {slide.text}
                </p>
                <a
                  href={generateProductLink(slide.productSlug)}
                  className="inline-flex items-center justify-center px-8 py-3 bg-primary-600 text-white font-bold text-base md:text-lg uppercase tracking-wide border-none rounded-none transition-colors duration-200 hover:bg-primary-700 active:bg-yellow-400 active:text-black focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ boxShadow: "none" }}
                >
                  Shop Now
                </a>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default HeroSlider;
